import {React, StyledProps, observer, classObserver, useContext, useLocalStore, stringify, pluralize, classNames, Fragment, useEffect} from '@api'
import {Icon} from '@blueprintjs/core'
import {Tooltip2} from '@api'
import {TabNode} from '@flexlayout'
import {IFlexLayoutCatalogRootConfig} from '@stores/catalog/flexLayout'
import {Drawer, PrimaryIconButton} from 'centric-ui-components'
import {DetailViewLocationIconButton, SidebarAggregateDetails, useStores, useVipLocalStore, VipLocalStore} from '@design-board/components'
import {ui, DetailsViewButtonType, useCatalog, globalStores, site, StoresInstance, Workspace, IFlexLayoutTabProps} from '@stores'
import {computed, toJS} from 'mobx'
import * as css from './RightSidebar.css'
import {Tooltip} from '@material-ui/core'
import {CatalogItemSidebarDetailsLoader} from '@lazy'
import FlipMove from 'react-flip-move'

interface MyProps extends StyledProps {
  isGlobalSidebar?: boolean
}

export const RightSidebar = observer<MyProps>(function RightSidebar({isGlobalSidebar, ...props}) {
  const stores = useStores()

  if (stores.isGlobalStore) return null

  const {
      sidebar: {right, right: {showImageFullscreen, inlineModal, variant, type, onClose, everOpen}},
      flexLayout: {isMaximized},
      catalog: {isShowingClientPDF, aggregation: {isShowingAggregateDetails, aggregateLocation, closeAggregateDetails}},
      purpose
    } = stores,
    isOpen = !!right.open && !_.isEmpty(right.path)

  if (!isGlobalSidebar && !showImageFullscreen) {
    return <div className={classNames(css.root)}
                data-inline-modal={inlineModal}
                data-type={type}
                data-sidebar-type={type}
    >
      <RightSidebarBody/>
    </div>
  }

  if (isGlobalSidebar && (isShowingClientPDF || purpose === 'inline-details')) return null /** We show it inline instead {@see CatalogRootFlexLayoutStore.children} **/

  return (
    <Fragment>
      <Drawer
        open={(type === 'sidebar') || (isOpen && (type == 'modal' || (!isShowingAggregateDetails && isMaximized)))}
        align={'right'}
        data-inline-modal={inlineModal}
        variant={type == 'modal' ? (inlineModal ? 'persistent' : 'temporary') : variant}
        data-type={type}
        onClose={onClose}
        //ModalProps={{disableBackdropClick: type == 'modal'}}
        data-sidebar-type={type}
        className={css.root}
        ModalProps={{disableEscapeKeyDown: true}}
        disableEscapeKeyDown={true} // To prevent closing the dialog when escape closing submenus or cancelling search or whatever
        classes={{
          drawerPaper: !showImageFullscreen || type === 'modal' ? css.drawerPaper : css.drawerPaperLightbox
        }}
        content={{
          titleComponent: <RightSidebarHeader/>,
          bodyComponent: everOpen ? <RightSidebarBody/> : null
        }}
      />

      {isShowingAggregateDetails && aggregateLocation == 'sidebar' && <Drawer
		  open={isShowingAggregateDetails}
		  align='right'
		  variant='permanent'
		  onClose={closeAggregateDetails}
		  className={css.aggregateDetails}
		  classes={{
            drawerPaper: css.aggregateDetailsPaper
          }}
		  content={{
            //titleComponent: <div>Header</div>,
            bodyComponent: <SidebarAggregateDetails/>
          }}
	  />}
    </Fragment>)
})

export const RightSidebarBody = observer<{ className?: string, topExtra?: React.ReactNode, flexNode?: TabNode<IFlexLayoutCatalogRootConfig> }>(function RightSidebarBody({className, topExtra, ...props}) {
  const {sidebar: {right, right: {_data, type, showDetailsView, isCreate, selectedPathItem, selectedLibraryConfig}}} = useStores(),
    {flexNode: tab} = props

  // useEffect(() => {
  //   console.log(`Mount RightSidebarBody`)
  //   return () => console.log(`Unmount RightSidebarBody`)
  // }, [])

  // useEffect(() => {
  //   if (!showDetailsView && tab) {
  //     tab.removeFromParent(true)
  //   }
  // }, [showDetailsView])

  return (
    showDetailsView && selectedPathItem
    ? <Fragment>
      <CatalogItemSidebarDetailsLoader
        //key={right.selectedIndex} // Don't force unmount/remount!
        debugId={`RightSidebarBody-${tab?.id}-${tab?.config?.settings?.workspace}-${right.selectedPathIndex}`}
        className={classNames(className, css.detailsLoader)}
        // Don't allow unload of modal when switching to preserve previous state
        //key={type == 'modal' ? 'modal' : JSON.stringify(selectedPathItem.where, null, 2)}  // Don't force an unmount/remount cycle
        align='right'
        context={selectedPathItem.libraryId ? 'libraries' : 'catalog'}
        esIndex={selectedPathItem?.esIndex}
        isCreate={isCreate}
        style={{'--sidebar-width': `${right.width}px`} as any}
        // library={{
        //   name: perspective?.field ? perspective.label : selectedPathItem.libraryId ?? boardConfig.itemName ?? 'right_sidebar',
        //   field: perspective?.field,
        //   keyword: perspective?.keyword ? true : undefined,
        //   detailView: perspective?.detailView ?? catalog.index,
        //   index: catalog.index,
        //   _source: perspective?.field ? [perspective.field] : undefined,
        //   nestedField: perspective?.idField,
        // }}
        library={selectedLibraryConfig}
        _data={isCreate ? _data : null}
        idWithPath={selectedPathItem?.idWithPath}
        catalogField={selectedPathItem?.catalogField}
        where={selectedPathItem.where}
      />
    </Fragment> : <div data-show-details-view={showDetailsView}
                       data-selected-path-item={JSON.stringify(selectedPathItem)}>
      No Data to Load
    </div>
  )
})

class RightSidebarHeaderStore extends VipLocalStore {
  constructor(stores: StoresInstance) {
    super(stores)
  }

  @computed get sidebarHeaderButtons() {
    const {catalog} = this
    return catalog?.detailViewFor(catalog?.index).sidebar?.header?.buttons
  }

  @computed get actionButtons() {
    const {catalog: {dialogs, sidebar: {right, right: {type}}}, catalog} = this

    return type == 'sidebar' ? _.chain(this.sidebarHeaderButtons).map(({label, type}: { label: string, type: DetailsViewButtonType }) => {
      switch (type) {
        case 'duplicate':
          const {details: {data}} = right
          return catalog.isActionButtonEnabled('duplicate', data) ? (
            <Fragment key={type}>
              <PrimaryIconButton wrapperClassName={css.duplicateButton}
                                 icon={<Icon icon='duplicate'/>}
                                 onClick={async () => {
                                   dialogs.showConfirmationProductPopup({
                                     catalog,
                                     message: `Are you sure you want to duplicate this '${data.name}' ${catalog.catalogItemName}?`,
                                     operation: 'DUPLICATE_SIDEBAR_ITEM',
                                     id: data._id
                                   })
                                 }}/>
            </Fragment>
          ) : null
      }
    }).compact().value() : []
  }
}

const RightSidebarHeaderContext = React.createContext<RightSidebarHeaderStore>(undefined)
const useRightSidebarHeaderStore = () => useContext(RightSidebarHeaderContext)

export const RightSidebarHeader = observer<{ className?: string }>(function RightSidebarHeader({className}) {
  const {workspaceConfig} = useCatalog(),
    store = useVipLocalStore((ws) => new RightSidebarHeaderStore(ws)),
    {stores: {sidebar: {right: {type, variant, toggleOpen, selectedLibrary}}}, actionButtons} = store

  return <div className={classNames(css.drawerHeader, className)}>
    <GoBackButton/>
    <span className={css.title}>{selectedLibrary ? selectedLibrary?.label : workspaceConfig?.itemName ?? '...'}</span>
    <span className={css.buttons}>
      {actionButtons}
      <DetailViewLocationIconButton/>
      {((variant == 'persistent' || variant == 'permanent') || (type == 'modal')) &&
	   <PrimaryIconButton wrapperClassName={css.closeButton}
	                      icon={<Icon icon='cross'/>}
	                      onClick={toggleOpen}/>}
    </span>
  </div>
})

const GoBackButton = observer(function GoBackButton() {
  const {strings, sidebar: {right: {onGoBack, path}}} = useStores()

  if (path.length <= 1) return null
  return (
    <Tooltip2 className={css.goBack} content={strings.goBack}>
      <PrimaryIconButton icon={<Icon icon='arrow-left'/>}
                         onClick={onGoBack}/>
    </Tooltip2>
  )
})


// WEBPACK FOOTER //
// ./src/modules/drawer/components/RightSidebar.tsx